/**
 * Third-party marketing scripts for the public pages: Osano (cookie consent) on the landing,
 * sign-up and login pages, and on the landing and sign-up pages also the HubSpot tracking
 * code and the RudderStack SDK, with the events the trial funnel reports through them. Osano
 * is loaded first and the trackers only once it has loaded, since it decides which of them
 * may run; if it does not load, the trackers are not loaded either.
 *
 * The portal is a single-page app, so a script loaded on a public page outlives the move
 * into the product after a sign-in. Signed-in pages load none of this, and Osano's banner and
 * widget are hidden while the user is signed in (setCookieConsentVisible).
 *
 * Nothing here may throw or block: a marketing script that is slow, missing or broken costs
 * that visit its analytics, never the visitor the sign-up.
 */

export const OSANO_SCRIPT_ID = 'osano-cmp'
export const OSANO_SRC = 'https://cmp.osano.com/169nu5TMub7Nk2Epy/7c82a576-285f-4b1c-8d4b-a1a4da5b7346/osano.js'

export const HUBSPOT_PORTAL_ID = '20265280'
/**
 * The Agentic Packr trial form. The sign-up page reads the visitor's country from this
 * form's embed definition; the backend's own setting decides which form the sign-up is
 * submitted to, so the two need not match.
 */
export const HUBSPOT_TRIAL_FORM_GUID = 'a7e8a2c5-95bc-4eda-a753-89e49098eb7c'
export const HUBSPOT_TRACKING_SCRIPT_ID = 'hs-script-loader'
export const HUBSPOT_TRACKING_SRC = `//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`
/** The cookie the HubSpot tracking code sets; its value is the form submission's `hutk`. */
export const HUBSPOT_TRACKING_COOKIE = 'hubspotutk'

export const RUDDERSTACK_SNIPPET_ID = 'rudderstack-sdk'
/** RudderStack's JavaScript SDK loader (snippet version 3.0.25) as supplied by marketing, verbatim. */
export const RUDDERSTACK_SNIPPET =
  `!(function(){'use strict';window.RudderSnippetVersion='3.0.25';var e='rudderanalytics';window[e]||(window[e]=[]);var rudderanalytics=window[e];if(Array.isArray(rudderanalytics)){if(true===rudderanalytics.snippetExecuted&&window.console&&console.error){console.error('RudderStack JavaScript SDK snippet included more than once.')}else{(rudderanalytics.snippetExecuted=true),(window.rudderAnalyticsBuildType='legacy');var sdkBaseUrl='https://rs-cdn.splashtop.com/v3';var sdkName='rsa.min.js';var scriptLoadingMode='async';var n=['setDefaultInstanceKey','load','ready','page','track','identify','alias','group','reset','setAnonymousId','startSession','endSession','consent'];for(var r=0;r<n.length;r++){var t=n[r];rudderanalytics[t]=(function(n){return function(){var r;Array.isArray(window[e])?rudderanalytics.push([n].concat(Array.prototype.slice.call(arguments))):null===(r=window[e][n])||void 0===r||r.apply(window[e],arguments)}})(t)}try{new Function('return import("")'),(window.rudderAnalyticsBuildType='modern')}catch(o){}var d=document.head||document.getElementsByTagName('head')[0];var i=document.body||document.getElementsByTagName('body')[0];(window.rudderAnalyticsAddScript=function(e,n,r){var t=document.createElement('script');(t.src=e),t.setAttribute('data-loader','RS_JS_SDK'),n&&r&&t.setAttribute(n,r),'async'===scriptLoadingMode?(t.async=true):'defer'===scriptLoadingMode&&(t.defer=true),d?d.insertBefore(t,d.firstChild):i.insertBefore(t,i.firstChild)}),(window.rudderAnalyticsMount=function(){!(function(){if('undefined'==typeof globalThis){var e;var n=(function getGlobal(){return'undefined'!=typeof self?self:'undefined'!=typeof window?window:null})();n&&Object.defineProperty(n,'globalThis',{value:n,configurable:true})}})(),window.rudderAnalyticsAddScript(''.concat(sdkBaseUrl,'/').concat(window.rudderAnalyticsBuildType,'/').concat(sdkName),'data-rsa-write-key','2lFZzMZDmh0eamxO0HvW5TVR5i8',)}),'undefined'==typeof Promise||'undefined'==typeof globalThis?window.rudderAnalyticsAddScript('https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount',):window.rudderAnalyticsMount();var loadOptions={configUrl:"https://rs-api.splashtop.com",onLoaded:function(rudderanalytics){window.rudderanalytics&&window.rudderanalytics.page(document.title);console.log('rudderanalytics.page event sent')}};rudderanalytics.load('2lFZzMZDmh0eamxO0HvW5TVR5i8','https://rs-dataplane.splashtop.com',loadOptions)}}})();`

/** How long a track event waits for the SDK before it is dropped, and how often it looks. */
const TRACK_GIVE_UP_MS = 9000
const TRACK_RETRY_MS = 300

/** Optimizely's visitor id: the website appends it to links here as `optid`; Optimizely reads it from the cookie. */
export const OPTIMIZELY_VISITOR_ID_PARAM = 'optid'
export const OPTIMIZELY_VISITOR_ID_COOKIE = 'optimizelyEndUserId'
const OPTIMIZELY_VISITOR_ID_RE = /^[A-Za-z0-9._-]{1,64}$/
const OPTIMIZELY_COOKIE_DAYS = 60

declare global {
  interface Window {
    rudderanalytics?: { track?: (event: string, properties?: Record<string, unknown>) => void }
    Osano?: { cm?: { showWidget?: () => void; hideWidget?: () => void; hideDialog?: () => void } }
  }
}

export function readCookie(name: string): string | undefined {
  const prefix = `${name}=`
  try {
    for (const part of document.cookie.split(';')) {
      const cookie = part.trim()
      if (!cookie.startsWith(prefix)) continue
      const raw = cookie.slice(prefix.length)
      try {
        return decodeURIComponent(raw) || undefined
      } catch {
        return raw || undefined
      }
    }
  } catch {
    // Cookies inaccessible (blocked by the browser): there is no value to read.
  }
  return undefined
}

/** The HubSpot tracking cookie, under HubSpot's own name or the `hutk` name the website's script reads. */
export function hubspotTrackingCookie(): string | undefined {
  return readCookie(HUBSPOT_TRACKING_COOKIE) ?? readCookie('hutk')
}

function insertTrackers(): void {
  const head = document.head
  if (!document.getElementById(HUBSPOT_TRACKING_SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = HUBSPOT_TRACKING_SCRIPT_ID
    script.type = 'text/javascript'
    script.async = true
    script.defer = true
    script.src = HUBSPOT_TRACKING_SRC
    head.appendChild(script)
  }
  if (!document.getElementById(RUDDERSTACK_SNIPPET_ID)) {
    const script = document.createElement('script')
    script.id = RUDDERSTACK_SNIPPET_ID
    script.text = RUDDERSTACK_SNIPPET
    head.appendChild(script)
  }
}

/** The Osano script element, inserted on first use; its `data-state` records whether it loaded. */
function osanoScript(): HTMLScriptElement {
  const existing = document.getElementById(OSANO_SCRIPT_ID) as HTMLScriptElement | null
  if (existing) return existing
  const osano = document.createElement('script')
  osano.id = OSANO_SCRIPT_ID
  osano.src = OSANO_SRC
  osano.addEventListener('load', () => {
    osano.dataset.state = 'loaded'
  })
  osano.addEventListener('error', () => {
    osano.dataset.state = 'failed'
    console.warn('Osano did not load')
  })
  document.head.appendChild(osano)
  return osano
}

/** Load Osano (cookie consent) once for this page load, on a public page. */
export function loadCookieConsent(): void {
  try {
    osanoScript()
  } catch (err) {
    console.warn('Cookie consent not loaded:', err)
  }
}

/**
 * Load Osano, then the HubSpot tracking code and the RudderStack SDK, once for this page
 * load. The trackers wait for Osano so it can govern them; an Osano that fails to load
 * leaves them out.
 */
export function loadMarketingTrackers(): void {
  try {
    const osano = osanoScript()
    const insert = () => {
      try {
        insertTrackers()
      } catch (err) {
        console.warn('Marketing trackers not loaded:', err)
      }
    }
    if (osano.dataset.state === 'loaded') insert()
    else if (osano.dataset.state !== 'failed') osano.addEventListener('load', insert, { once: true })
  } catch (err) {
    console.warn('Marketing trackers not loaded:', err)
  }
}

/**
 * Show or hide Osano's banner and widget. Inside the product (a signed-in user) they stay
 * hidden: the attribute on the document root backs a stylesheet rule, so the hide holds even
 * while Osano is still loading, and Osano's own API is used once it is there.
 */
export function setCookieConsentVisible(visible: boolean): void {
  try {
    const root = document.documentElement
    if (visible) delete root.dataset.cookieConsent
    else root.dataset.cookieConsent = 'hidden'
    const cm = window.Osano?.cm
    if (!cm) return
    if (visible) {
      cm.showWidget?.()
    } else {
      cm.hideDialog?.()
      cm.hideWidget?.()
    }
  } catch (err) {
    console.warn('Cookie consent visibility not changed:', err)
  }
}

/** Send a RudderStack track event, waiting up to 9 seconds for the SDK to be ready. Never throws. */
export function sendRudderStackTrackEvent(event: string, properties?: Record<string, unknown>): void {
  const giveUp = Date.now() + TRACK_GIVE_UP_MS
  const attempt = () => {
    try {
      if (typeof window.rudderanalytics?.track === 'function') {
        console.log('Sending event:', { event, properties })
        window.rudderanalytics.track(event, properties)
      } else if (Date.now() < giveUp) {
        window.setTimeout(attempt, TRACK_RETRY_MS)
      } else {
        console.warn('RudderStack not ready, dropped track event:', event)
      }
    } catch (err) {
      console.warn('RudderStack track event failed:', event, err)
    }
  }
  attempt()
}

/**
 * Optimizely cross-domain tracking, as the website's script does it: a link from the website
 * carries the visitor's Optimizely id as `optid`; when it differs from the cookie, the cookie
 * is written on the site's registrable domain and on this host's parent domain, so every
 * Splashtop host sees one visitor. Runs on every page load; never throws.
 */
export function fixOptimizelyCrossDomainTracking(
  search: string = window.location.search,
  hostname: string = window.location.hostname,
): void {
  try {
    const id = new URLSearchParams(search).get(OPTIMIZELY_VISITOR_ID_PARAM)
    if (!id || !OPTIMIZELY_VISITOR_ID_RE.test(id) || readCookie(OPTIMIZELY_VISITOR_ID_COOKIE) === id) return
    const labels = hostname.split('.')
    const expires = new Date(Date.now() + OPTIMIZELY_COOKIE_DAYS * 24 * 60 * 60 * 1000).toUTCString()
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    for (const domain of new Set([labels.slice(-2).join('.'), labels.slice(-3).join('.')])) {
      document.cookie = `${OPTIMIZELY_VISITOR_ID_COOKIE}=${id}; domain=${domain}; path=/; expires=${expires}; SameSite=Lax${secure}`
    }
  } catch {
    // A cookie that cannot be written is Optimizely's loss, not the page's.
  }
}
